// ts-rest contract for the DESK side of Transfers (SEP-283): "my transfers",
// the checklist prefill, the new ticket, a Desk note and the return to
// Operations.
//
// The Transfer updates of the caller (SEP-317 section 7) are under
// `/transfer-updates`, so the paths never match `/transfers/:id`. They are the
// notifications of the caller with the subject kind `transfer_request`.
//
// Every read is scoped to the book of the caller. The server takes the book
// from the session, so no request here holds an IM id.
//
// Four routes are raw Nest and not ts-rest, because ts-rest is JSON-only:
//   POST   /api/transfers/:id/documents                       multipart upload
//   GET    /api/transfers/:id/documents/:documentId/content   bytes
//   POST   /api/transfer-drafts/documents                     a staged upload of
//          the checklist (SEP-317); the answer is `transferDraftDocumentSchema`
//   DELETE /api/transfer-drafts/documents/:sourceUploadId     remove a staged upload

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { errorBody, errorBodyWithCode } from '../schemas/common';
import { userNotificationsResponseSchema } from '../schemas/user-notifications';
import {
    challengeTransferCompletionBodySchema,
    createTransferRequestBodySchema,
    createTransferRequestResponseSchema,
    deskAddTransferNoteBodySchema,
    deskTransferDetailSchema,
    deskTransferListResponseSchema,
    editTransferNoteBodySchema,
    respondToTransferHoldingBodySchema,
    returnTransferToOperationsBodySchema,
    transferBookPortfoliosResponseSchema,
    transferRequestPrefillSchema,
    transferSecuritySearchResponseSchema,
} from '../schemas/transfers';

const c = initContract();

const idParams = z.object({ id: z.string() });
const noteParams = z.object({ id: z.string(), noteId: z.string() });

export const transfersContract = c.router(
    {
        listMyTransfers: {
            method: 'GET',
            path: '/transfers',
            responses: { 200: deskTransferListResponseSchema, 403: errorBody },
            summary: 'The transfer tickets of the book of the caller, newest first',
        },
        prefill: {
            method: 'GET',
            path: '/transfers/prefill/:clientId',
            pathParams: z.object({ clientId: z.string() }),
            responses: { 200: transferRequestPrefillSchema, 403: errorBody, 404: errorBody },
            summary: 'The checklist answers that the client record already gives',
        },
        bookPortfolios: {
            method: 'GET',
            path: '/transfers/book-portfolios',
            responses: { 200: transferBookPortfoliosResponseSchema, 403: errorBody },
            summary: 'The portfolios of each client of the book of the caller (SEP-359)',
        },
        searchSecurities: {
            method: 'GET',
            path: '/transfers/securities',
            query: z.object({ q: z.string().min(1).max(100) }),
            responses: { 200: transferSecuritySearchResponseSchema, 403: errorBody },
            summary: 'Search a security by name, ISIN or ticker, for the stock of the checklist',
        },
        getMyTransfer: {
            method: 'GET',
            path: '/transfers/:id',
            pathParams: idParams,
            responses: { 200: deskTransferDetailSchema, 403: errorBody, 404: errorBody },
            summary: 'One transfer ticket, as the Desk sees it',
        },
        createTransfer: {
            method: 'POST',
            path: '/transfers',
            body: createTransferRequestBodySchema,
            responses: {
                201: createTransferRequestResponseSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Submit a transfer checklist to Operations',
        },
        addNote: {
            method: 'POST',
            path: '/transfers/:id/notes',
            pathParams: idParams,
            body: deskAddTransferNoteBodySchema,
            responses: {
                200: deskTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Add a note that the Desk and Operations both see',
        },
        editNote: {
            method: 'PATCH',
            path: '/transfers/:id/notes/:noteId',
            pathParams: noteParams,
            body: editTransferNoteBodySchema,
            responses: {
                200: deskTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Edit the text of a note that the caller wrote (SEP-359)',
        },
        removeNote: {
            method: 'POST',
            path: '/transfers/:id/notes/:noteId/remove',
            pathParams: noteParams,
            body: z.object({}),
            responses: {
                200: deskTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Remove a note that the caller wrote from the notes lists (SEP-359)',
        },
        returnToOperations: {
            method: 'POST',
            path: '/transfers/:id/return-to-operations',
            pathParams: idParams,
            body: returnTransferToOperationsBodySchema,
            responses: {
                200: deskTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Give a ticket that Operations returned back to Operations',
        },
        confirmSecuritiesSold: {
            method: 'POST',
            path: '/transfers/:id/securities-sale/confirm',
            pathParams: idParams,
            body: z.object({}),
            responses: {
                200: deskTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Confirm that the sell orders of a full transfer in cash are placed (SEP-359)',
        },
        challengeCompletion: {
            method: 'POST',
            path: '/transfers/:id/challenge',
            pathParams: idParams,
            body: challengeTransferCompletionBodySchema,
            responses: {
                200: deskTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary: 'Challenge the completion of a complete ticket, with an explanation',
        },
        respondToHolding: {
            method: 'POST',
            path: '/transfers/:id/holdings/:holdingId/respond',
            pathParams: z.object({ id: z.string(), holdingId: z.string() }),
            body: respondToTransferHoldingBodySchema,
            responses: {
                200: deskTransferDetailSchema,
                400: errorBodyWithCode,
                403: errorBody,
                404: errorBody,
                409: errorBodyWithCode,
            },
            summary:
                'Respond to Operations on a holding line that Operations referred to the IM (SEP-359)',
        },
        listUpdates: {
            method: 'GET',
            path: '/transfer-updates',
            responses: { 200: userNotificationsResponseSchema, 403: errorBody },
            summary: 'The Transfer updates of the caller, newest first',
        },
        markUpdateRead: {
            method: 'POST',
            path: '/transfer-updates/:id/read',
            pathParams: idParams,
            body: z.object({}).optional(),
            responses: { 200: z.object({ ok: z.literal(true) }), 403: errorBody, 404: errorBody },
            summary: 'Mark one Transfer update of the caller as read',
        },
        markAllUpdatesRead: {
            method: 'POST',
            path: '/transfer-updates/read-all',
            body: z.object({}).optional(),
            responses: {
                200: z.object({ ok: z.literal(true), marked: z.number().int() }),
                403: errorBody,
            },
            summary: 'Mark every Transfer update of the caller as read',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
